import { Role, Permission } from './permissions.types'

/**
 * Opt-in permissions - sensitive permissions that are OFF by default for ALL roles
 * (including admin) and require explicit grant via additional_permissions.
 *
 * NOTE: super_user has ALL permissions including these opt-in ones.
 *
 * These will always be visible in the permissions UI for granting to users.
 */
export const OPT_IN_PERMISSIONS: Permission[] = [
  'email.crm_lead',
]

/**
 * Super-user-only permissions — never grantable by admin/manager, regardless
 * of any other rule. Membership here forces:
 *   1. `validatePermissionGrant` to deny grants from non-super-user grantors.
 *   2. `getEffectivePermissions` to NOT spread the permission into admin's
 *      effective set via the viewer-grantable shortcut.
 *
 * MUST NOT be added to OPT_IN_PERMISSIONS or VIEWER_GRANTABLE_PERMISSIONS,
 * because both are spread into admin's grantable surface.
 */
export const SUPER_USER_ONLY_PERMISSIONS: Permission[] = [
  'demo_mode.manage',
  'ai.providers.manage',
  'ai.models.manage',
  'ai.global_configs.manage',
  // Per-agent model override: lets a user re-point a specific agent to a
  // non-default model. Sensitive (cost/quality impact), so super_user-grant-only.
  'ai.agent_model_override',
  'tenants.purge',
  // Cross-tenant / platform-wide family. These operate across ALL tenants, so
  // only super_user may grant/revoke them, and (enforced separately at the data
  // layer via requirePlatformPermission / isPlatformTenant) they only take
  // effect for platform-tenant users. Defense-in-depth: this list blocks
  // delegation by admin/integrator and wildcard pickup; it does NOT retroactively
  // revoke an already-granted explicit entry — the endpoint platform gate does.
  'tenants.view',
  'tenants.create',
  'billing.view_all',
  'billing.view_tenants',
  'billing.manage_plans',
  'billing.manage_tenants',
  'billing.grant_credits',
  'billing.manage_elevenlabs_pool',
  'system_settings.view',
  'system_settings.edit',
  'partner_program.admin',
  'system_notifications.manage',
]

/**
 * Viewer-grantable permissions - permissions that can be granted to viewers.
 * These are permissions that admin/manager have by role and can delegate to viewers.
 * Unlike OPT_IN, these work with super_user wildcard and role-based grants.
 */
export const VIEWER_GRANTABLE_PERMISSIONS: Permission[] = [
  'voice_agents.view',
  'voice_agents.manage',
  'email_agents.view',
  'email_agents.manage',
  'email_agents.analytics',
  'messaging_agents.view',
  'messaging_agents.manage',
  'messaging_agents.analytics',
  'notifications.manage',
  'users.manage_permissions',
  'shopify.view',
  'crm.view',
  'deliveries.view',
  'deliveries.reconcile',
  'agent_tasks.view',
  'agent_tasks.manage',
  ...OPT_IN_PERMISSIONS,
]

export const USERS_PERMISSIONS = {
  VIEW: 'users.view',
  CREATE: 'users.create',
  EDIT: 'users.edit',
  DELETE: 'users.delete',
  MANAGE_PERMISSIONS: 'users.manage_permissions',
} as const satisfies Record<string, Permission>

export const EMAIL_PERMISSIONS = {
  VIEW: 'email.view',
  MANAGE: 'email.manage',
  SYNC: 'email.sync',
  QA_FEEDBACK: 'email.qa_feedback',
  CRM_LEAD: 'email.crm_lead',
} as const satisfies Record<string, Permission>

export const INTEGRATIONS_PERMISSIONS = {
  VIEW: 'integrations.view',
  MANAGE: 'integrations.*',
  GOOGLE_MANAGE: 'integrations.google.manage',
  KOMMO_MANAGE: 'integrations.kommo.manage',
  SHOPIFY_MANAGE: 'integrations.shopify.manage',
  SHOPIFY_DELETE_DATA: 'integrations.shopify.delete_data',
} as const satisfies Record<string, Permission>

export const TENANTS_PERMISSIONS = {
  VIEW: 'tenants.view',
  CREATE: 'tenants.create',
  // Own-tenant visibility: gates the Organization tab + the
  // `/api/tenants/current/stats` endpoint (users count / active agents /
  // wallet balance / current plan). Admin gets it by default; can be
  // delegated to specific non-admin users via `additional_permissions`
  // (e.g. ops or finance staff who need the org dashboard but not the
  // edit / archive surface).
  VIEW_OWN: 'tenants.view_own',
  // Own-tenant lifecycle (admin + owner check in endpoints; super_user covered by wildcard)
  EDIT_OWN: 'tenants.edit_own',
  ARCHIVE: 'tenants.archive',
  // Immediate hard-delete that bypasses the archive window — super_user only
  PURGE: 'tenants.purge',
} as const satisfies Record<string, Permission>

export const SHOPIFY_PERMISSIONS = {
  VIEW: 'shopify.view',
} as const satisfies Record<string, Permission>

export const CRM_PERMISSIONS = {
  VIEW: 'crm.view',
} as const satisfies Record<string, Permission>

export const DELIVERIES_PERMISSIONS = {
  VIEW: 'deliveries.view',
  RECONCILE: 'deliveries.reconcile',
} as const satisfies Record<string, Permission>

export const VOICE_AGENTS_PERMISSIONS = {
  VIEW: 'voice_agents.view',
  MANAGE: 'voice_agents.manage',
} as const satisfies Record<string, Permission>

export const EMAIL_AGENTS_PERMISSIONS = {
  VIEW: 'email_agents.view',
  MANAGE: 'email_agents.manage',
  ANALYTICS: 'email_agents.analytics',
} as const satisfies Record<string, Permission>

export const CALLS_PERMISSIONS = {
  VIEW: 'calls.view',
  VIEW_ALL: 'calls.view_all',
  DELETE: 'calls.delete',
} as const satisfies Record<string, Permission>

export const AGENT_TASKS_PERMISSIONS = {
  VIEW: 'agent_tasks.view',
  MANAGE: 'agent_tasks.manage',
} as const satisfies Record<string, Permission>

export const AI_PERMISSIONS = {
  VIEW: 'ai.view',
  MANAGE_PROVIDERS: 'ai.providers.manage',
  MANAGE_MODELS: 'ai.models.manage',
  MANAGE_CONFIGS: 'ai.configs.manage',
  MANAGE_GLOBAL_CONFIGS: 'ai.global_configs.manage',
  // Per-agent model override for text agents (email/message). Grantable by
  // super_user only (see SUPER_USER_ONLY_PERMISSIONS); super_user has it via wildcard.
  AGENT_MODEL_OVERRIDE: 'ai.agent_model_override',
  VIEW_LOGS: 'ai.logs.view',
  VIEW_LOGS_TOOLS: 'ai.logs.view_tools',
  MANAGE_KNOWLEDGE_BASE: 'ai.knowledge_base.manage',
} as const satisfies Record<string, Permission>

export const PBX_PERMISSIONS = {
  VIEW: 'pbx.view',
  MANAGE: 'pbx.manage',
} as const satisfies Record<string, Permission>

export const MESSAGING_AGENTS_PERMISSIONS = {
  VIEW: 'messaging_agents.view',
  MANAGE: 'messaging_agents.manage',
  // Conversation Quality. Separate from VIEW because the numbers span every agent and
  // every conversation — the same split email analytics already makes.
  ANALYTICS: 'messaging_agents.analytics',
} as const satisfies Record<string, Permission>

export const NOTIFICATIONS_PERMISSIONS = {
  // Manage tenant notification channels (create/edit/delete/test email + Telegram
  // sinks). Tenant-level config shared by every feature that emits alerts, so it
  // is its own permission rather than reusing an agent/integrations one.
  MANAGE: 'notifications.manage',
} as const satisfies Record<string, Permission>

export const SYSTEM_NOTIFICATIONS_PERMISSIONS = {
  // Routes platform-operator alerts (a tenant registered, changed plan, …) to the platform
  // tenant's channels. Cross-tenant by nature, so super_user grants it and it only takes
  // effect for platform-tenant users — see SUPER_USER_ONLY_PERMISSIONS.
  MANAGE: 'system_notifications.manage',
} as const satisfies Record<string, Permission>

export const API_KEYS_PERMISSIONS = {
  // Create, list, and revoke tenant-scoped API keys.
  MANAGE: 'api_keys.manage',
} as const satisfies Record<string, Permission>

export const PARTNER_PROGRAM_PERMISSIONS = {
  /** Own cabinet. */
  MANAGE: 'partner_program.manage',
  /** Cross-tenant console; platform-tenant users only. */
  ADMIN: 'partner_program.admin',
} as const satisfies Record<string, Permission>

export const DEMO_MODE_PERMISSIONS = {
  MANAGE: 'demo_mode.manage',
} as const satisfies Record<string, Permission>

export const BILLING_PERMISSIONS = {
  // Tenant-level: view own billing, manage subscription
  VIEW: 'billing.view',
  MANAGE: 'billing.manage',

  // Super User only: cross-tenant billing management
  VIEW_ALL: 'billing.view_all',
  // Read-only access to the cross-tenant Tenants tab (list + balances/economics),
  // WITHOUT the Plan change / Actions columns. Narrower than VIEW_ALL (only the
  // Tenants tab) and read-only vs MANAGE_TENANTS (no override/convert/revert).
  VIEW_TENANTS: 'billing.view_tenants',
  MANAGE_PLANS: 'billing.manage_plans',
  MANAGE_TENANTS: 'billing.manage_tenants',
  GRANT_CREDITS: 'billing.grant_credits',
  MANAGE_ELEVENLABS_POOL: 'billing.manage_elevenlabs_pool',
} as const satisfies Record<string, Permission>

/**
 * System Settings permissions - SUPER_USER ONLY
 * These are global system-wide settings, not tenant-specific
 */
export const SYSTEM_SETTINGS_PERMISSIONS = {
  VIEW: 'system_settings.view',
  EDIT: 'system_settings.edit',
} as const satisfies Record<string, Permission>

/**
 * Admin permission set — the full tenant-admin surface.
 *
 * Extracted as a single source of truth so the `integrator` role (admin-level,
 * but cross-tenant) reuses the exact same set without duplication. Keep this as
 * the canonical "tenant-admin" list; do not let admin and integrator drift.
 */
export const ADMIN_PERMISSIONS: Permission[] = [
    'users.view',
    'users.create',
    'users.edit',
    'users.delete',
    USERS_PERMISSIONS.MANAGE_PERMISSIONS,
    // Own-tenant: view (Organization tab + stats endpoint), edit
    // name/logo/timezone, self-archive (endpoints additionally require
    // `tenant.owner_user_id === user.id` for archive).
    TENANTS_PERMISSIONS.VIEW_OWN,
    TENANTS_PERMISSIONS.EDIT_OWN,
    TENANTS_PERMISSIONS.ARCHIVE,
    'tenant_settings.view',
    'tenant_settings.edit',
    'tenant_settings.create',
    'tenant_settings.delete',
    'user_settings.view',
    'user_settings.create',
    'user_settings.edit',
    'user_settings.delete',
    // system_settings.* - SUPER_USER ONLY (not for admin)
    // Voice Agents - full access
    VOICE_AGENTS_PERMISSIONS.VIEW,
    VOICE_AGENTS_PERMISSIONS.MANAGE,
    // Email Agents - full access
    EMAIL_AGENTS_PERMISSIONS.VIEW,
    EMAIL_AGENTS_PERMISSIONS.MANAGE,
    EMAIL_AGENTS_PERMISSIONS.ANALYTICS,
    // Messaging Agents - full access
    MESSAGING_AGENTS_PERMISSIONS.VIEW,
    MESSAGING_AGENTS_PERMISSIONS.MANAGE,
    MESSAGING_AGENTS_PERMISSIONS.ANALYTICS,
    // Notification channels - manage tenant alerting sinks
    NOTIFICATIONS_PERMISSIONS.MANAGE,
    // Tenant API keys
    API_KEYS_PERMISSIONS.MANAGE,
    AGENT_TASKS_PERMISSIONS.VIEW,
    AGENT_TASKS_PERMISSIONS.MANAGE,
    PARTNER_PROGRAM_PERMISSIONS.MANAGE,
    // Calls
    'calls.view',
    'calls.view_all',
    'calls.delete',
    // Email operations
    EMAIL_PERMISSIONS.VIEW,
    EMAIL_PERMISSIONS.MANAGE,
    EMAIL_PERMISSIONS.SYNC,
    EMAIL_PERMISSIONS.QA_FEEDBACK,
    // Integrations
    INTEGRATIONS_PERMISSIONS.VIEW,
    INTEGRATIONS_PERMISSIONS.MANAGE,
    INTEGRATIONS_PERMISSIONS.GOOGLE_MANAGE,
    INTEGRATIONS_PERMISSIONS.KOMMO_MANAGE,
    INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE,
    INTEGRATIONS_PERMISSIONS.SHOPIFY_DELETE_DATA,
    SHOPIFY_PERMISSIONS.VIEW,
    CRM_PERMISSIONS.VIEW,
    DELIVERIES_PERMISSIONS.VIEW,
    DELIVERIES_PERMISSIONS.RECONCILE,
    // Analytics
    'analytics.view',
    'financial.view',
    // AI - admin can view/use AI features but not configure providers/models (System Settings only)
    AI_PERMISSIONS.VIEW,
    // AI_PERMISSIONS.MANAGE_PROVIDERS - super_user only (System Settings)
    // AI_PERMISSIONS.MANAGE_MODELS - super_user only (System Settings)
    AI_PERMISSIONS.MANAGE_CONFIGS, // tenant-scoped prompt/tool-instruction overrides
    // AI_PERMISSIONS.MANAGE_GLOBAL_CONFIGS - super_user only (platform defaults)
    AI_PERMISSIONS.VIEW_LOGS,
    AI_PERMISSIONS.VIEW_LOGS_TOOLS,
    AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE,
    // PBX - full access
    PBX_PERMISSIONS.VIEW,
    PBX_PERMISSIONS.MANAGE,
    // Billing - tenant-level access
    BILLING_PERMISSIONS.VIEW,
    BILLING_PERMISSIONS.MANAGE,
]

/**
 * Role-to-permissions mapping
 * Defines what permissions each role has by default
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  /**
   * Super User: Has all permissions (wildcard)
   * Can do everything in the system, including switching tenants
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- '*' wildcard is not a valid Permission enum value
  super_user: ['*'] as any,

  /**
   * Integrator: platform-tenant service role. Same permission surface as admin
   * (cross-tenant switching is granted via role-gating, NOT via permissions —
   * see canImpersonate / CROSS_TENANT_ROLES). Must stay identical to admin to
   * keep the "admin-level" guarantee true.
   */
  integrator: ADMIN_PERMISSIONS,

  /**
   * Admin: Full control within their tenant
   * Can manage users, settings, and all resources in their tenant
   */
  admin: ADMIN_PERMISSIONS,

  /**
   * Manager: Can manage operational resources but not users
   * Can view agents but not manage settings by default (opt-in)
   */
  manager: [
    'users.view',
    'user_settings.view',
    'user_settings.create',
    'user_settings.edit',
    'user_settings.delete',
    // system_settings.* - SUPER_USER ONLY (not for manager)
    'tenant_settings.view',
    // Voice/Email/Messaging Agents - view only, manage is opt-in
    VOICE_AGENTS_PERMISSIONS.VIEW,
    AGENT_TASKS_PERMISSIONS.VIEW,
    EMAIL_AGENTS_PERMISSIONS.VIEW,
    MESSAGING_AGENTS_PERMISSIONS.VIEW,
    // Calls
    'calls.view',
    'calls.view_all',
    // Email operations
    EMAIL_PERMISSIONS.VIEW,
    EMAIL_PERMISSIONS.MANAGE,
    EMAIL_PERMISSIONS.QA_FEEDBACK,
    // Analytics
    'analytics.view',
    'financial.view',
    // Integrations - view only
    INTEGRATIONS_PERMISSIONS.VIEW,
    DELIVERIES_PERMISSIONS.VIEW,
    DELIVERIES_PERMISSIONS.RECONCILE,
    // AI
    AI_PERMISSIONS.VIEW,
    AI_PERMISSIONS.VIEW_LOGS,
    AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE,
    // PBX - view only
    PBX_PERMISSIONS.VIEW,
    // Billing - view only
    BILLING_PERMISSIONS.VIEW,
  ],

  /**
   * Viewer: Minimal access by default - only profile
   * Everything else is opt-in via additional_permissions
   */
  viewer: [
    'user_settings.view',
    'user_settings.edit',
    // All other permissions are opt-in - admin/manager can grant individually
  ],
}

/**
 * Permission groups for easier management
 * Can be used to grant multiple related permissions at once
 */
export const PERMISSION_GROUPS = {
  FULL_VOICE_AGENTS_ACCESS: [
    VOICE_AGENTS_PERMISSIONS.VIEW,
    VOICE_AGENTS_PERMISSIONS.MANAGE,
  ] as Permission[],
  FULL_EMAIL_AGENTS_ACCESS: [
    EMAIL_AGENTS_PERMISSIONS.VIEW,
    EMAIL_AGENTS_PERMISSIONS.MANAGE,
    EMAIL_AGENTS_PERMISSIONS.ANALYTICS,
  ] as Permission[],
  FULL_MESSAGING_AGENTS_ACCESS: [
    MESSAGING_AGENTS_PERMISSIONS.VIEW,
    MESSAGING_AGENTS_PERMISSIONS.MANAGE,
    MESSAGING_AGENTS_PERMISSIONS.ANALYTICS,
  ] as Permission[],
  FULL_USERS_ACCESS: ['users.view', 'users.create', 'users.edit', 'users.delete', 'users.manage_permissions'] as Permission[],
  FULL_INTEGRATIONS_ACCESS: [
    INTEGRATIONS_PERMISSIONS.VIEW,
    INTEGRATIONS_PERMISSIONS.MANAGE,
    INTEGRATIONS_PERMISSIONS.GOOGLE_MANAGE,
    INTEGRATIONS_PERMISSIONS.KOMMO_MANAGE,
    INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE,
  ] as Permission[],
  FULL_TENANT_SETTINGS_ACCESS: ['tenant_settings.view', 'tenant_settings.edit', 'tenant_settings.create', 'tenant_settings.delete'] as Permission[],
  FULL_AI_ACCESS: [
    AI_PERMISSIONS.VIEW,
    AI_PERMISSIONS.MANAGE_PROVIDERS,
    AI_PERMISSIONS.MANAGE_MODELS,
    AI_PERMISSIONS.MANAGE_CONFIGS,
    AI_PERMISSIONS.MANAGE_GLOBAL_CONFIGS,
    AI_PERMISSIONS.VIEW_LOGS,
    AI_PERMISSIONS.VIEW_LOGS_TOOLS,
    AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE,
  ] as Permission[],
  FULL_BILLING_ACCESS: [
    BILLING_PERMISSIONS.VIEW,
    BILLING_PERMISSIONS.MANAGE,
  ] as Permission[],
  // Super User only - all billing permissions
  FULL_BILLING_ADMIN_ACCESS: [
    BILLING_PERMISSIONS.VIEW,
    BILLING_PERMISSIONS.MANAGE,
    BILLING_PERMISSIONS.VIEW_ALL,
    BILLING_PERMISSIONS.VIEW_TENANTS,
    BILLING_PERMISSIONS.MANAGE_PLANS,
    BILLING_PERMISSIONS.MANAGE_TENANTS,
    BILLING_PERMISSIONS.GRANT_CREDITS,
    BILLING_PERMISSIONS.MANAGE_ELEVENLABS_POOL,
  ] as Permission[],
}
